/**
 * @description
 * AlertSystem component for the MediConnect Healthcare Information System.
 * Provides a consistent interface for displaying notifications, alerts, and feedback to users.
 * This component leverages react-toastify for elegant, accessible notifications.
 * 
 * @dependencies
 * - react-toastify: For notification management
 * - ../styles/alerts.css: For custom styling
 */

import React from 'react';
import { ToastContainer, toast, ToastOptions, Id, ToastContainerProps } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Configuration for different alert types
const TOAST_CONFIG: ToastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
};

// Configuration for HIPAA-related alerts (stays longer, more prominent)
const HIPAA_TOAST_CONFIG: ToastOptions = {
  ...TOAST_CONFIG,
  autoClose: 8000,
  className: 'hipaa-alert',
};

// Keep track of active toasts to prevent duplicates
let activeToasts = new Set<string>();

/**
 * Displays a success notification
 * @param {string} message - The message to display
 * @param {Object} options - Additional options for the toast
 */
export const showSuccess = (message: string, options: ToastOptions = {}): Id => {
  // Check if this message is already being shown
  if (activeToasts.has(message)) {
    return "" as Id; // Don't show duplicate toast, return empty ID
  }
  
  const id = toast.success(message, { ...TOAST_CONFIG, ...options });
  activeToasts.add(message);
  
  // Remove from tracking after it closes
  setTimeout(() => {
    activeToasts.delete(message);
  }, (options.autoClose || TOAST_CONFIG.autoClose || 5000) + 100);
  
  return id;
};

/**
 * Displays an error notification
 * @param {string} message - The error message to display
 * @param {Object} options - Additional options for the toast
 */
export const showError = (message: string, options: ToastOptions = {}): Id => {
  // Check if this message is already being shown
  if (activeToasts.has(message)) {
    return "" as Id; // Don't show duplicate toast, return empty ID
  }
  
  const id = toast.error(message, { ...TOAST_CONFIG, ...options });
  activeToasts.add(message);
  
  // Remove from tracking after it closes
  setTimeout(() => {
    activeToasts.delete(message);
  }, (options.autoClose || TOAST_CONFIG.autoClose || 5000) + 100);
  
  return id;
};

/**
 * Displays an information notification
 * @param {string} message - The information message to display
 * @param {Object} options - Additional options for the toast
 */
export const showInfo = (message: string, options: ToastOptions = {}): Id => {
  // Check if this message is already being shown
  if (activeToasts.has(message)) {
    return "" as Id; // Don't show duplicate toast, return empty ID
  }
  
  const id = toast.info(message, { ...TOAST_CONFIG, ...options });
  activeToasts.add(message);
  
  // Remove from tracking after it closes
  setTimeout(() => {
    activeToasts.delete(message);
  }, (options.autoClose || TOAST_CONFIG.autoClose || 5000) + 100);
  
  return id;
};

/**
 * Displays a HIPAA-compliant notification for sensitive health information
 * @param {string} message - The HIPAA-related message
 * @param {Object} options - Additional options for the toast
 */
export const showHipaaAlert = (message: string, options: ToastOptions = {}): Id => {
  // Check if this message is already being shown
  if (activeToasts.has(message)) {
    return "" as Id; // Don't show duplicate toast, return empty ID
  }
  
  const id = toast.info(message, { ...HIPAA_TOAST_CONFIG, ...options });
  activeToasts.add(message);
  
  // Remove from tracking after it closes
  setTimeout(() => {
    activeToasts.delete(message);
  }, (options.autoClose || HIPAA_TOAST_CONFIG.autoClose || 8000) + 100);
  
  return id;
};

// Define appointment interface
interface Appointment {
  doctorName: string;
  date: string | Date;
  locationName?: string;
  isVirtual?: boolean;
}

/**
 * Displays a specialized notification for successful appointment booking
 * @param {Object} appointment - The appointment details
 * @param {Function} viewCallback - Callback function to view appointment details
 * @param {Object} options - Additional options for the toast
 */
export const showAppointmentConfirmation = (
  appointment: Appointment, 
  viewCallback: () => void, 
  options: ToastOptions = {}
): Id => {
  // Create a unique key for this appointment
  const toastKey = `appointment-${appointment.doctorName}-${appointment.date}`;
  
  // Check if this appointment confirmation is already being shown
  if (activeToasts.has(toastKey)) {
    return "" as Id; // Don't show duplicate toast, return empty ID
  }
  
  // Create a custom component for the appointment confirmation
  const AppointmentConfirmation = () => {
    // Format date and time
    const appointmentDate = new Date(appointment.date);
    const formattedDate = appointmentDate.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric'
    });
    const formattedTime = appointmentDate.toLocaleTimeString('en-US', { 
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    return (
      <div className="appointment-confirmation-content">
        <div className="appointment-confirmation-header">
          <div className="appointment-confirmation-header-icon">✓</div>
          <h3 className="appointment-confirmation-header-title">Appointment Confirmed</h3>
        </div>
        
        <div className="appointment-confirmation-body">
          <div className="appointment-confirmation-details">
            <div className="appointment-confirmation-detail">
              <svg className="appointment-confirmation-detail-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="appointment-confirmation-detail-text">
                {appointment.doctorName}
              </span>
            </div>
            
            <div className="appointment-confirmation-detail">
              <svg className="appointment-confirmation-detail-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="appointment-confirmation-detail-text">
                {formattedDate}
              </span>
            </div>
            
            <div className="appointment-confirmation-detail">
              <svg className="appointment-confirmation-detail-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="appointment-confirmation-detail-text">
                {formattedTime}
              </span>
            </div>
            
            {appointment.locationName && !appointment.isVirtual && (
              <div className="appointment-confirmation-detail">
                <svg className="appointment-confirmation-detail-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="appointment-confirmation-detail-text">
                  {appointment.locationName}
                </span>
              </div>
            )}
            
            {appointment.isVirtual && (
              <div className="appointment-confirmation-detail">
                <svg className="appointment-confirmation-detail-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span className="appointment-confirmation-detail-text">
                  Virtual Visit
                </span>
              </div>
            )}
          </div>
        </div>
        
        <div className="appointment-confirmation-footer">
          <div>
            <a className="appointment-confirmation-action" onClick={viewCallback}>
              View Details
            </a>
          </div>
          <div>
            <a className="appointment-confirmation-action" onClick={() => toast.dismiss()}>
              Dismiss
            </a>
          </div>
        </div>
      </div>
    );
  };
  
  const toastId = toast(
    <AppointmentConfirmation />,
    { 
      ...TOAST_CONFIG,
      autoClose: 10000, // Show for 10 seconds
      className: 'appointment-confirmation-toast',
      closeButton: false,
      ...options
    }
  );
  
  activeToasts.add(toastKey);
  
  // Remove from tracking after it closes
  setTimeout(() => {
    activeToasts.delete(toastKey);
  }, (options.autoClose || 10000) + 100);
  
  return toastId;
};

/**
 * AlertSystem Functional Component
 * Renders the ToastContainer to display notifications.
 * Configures react-toastify with application-specific settings.
 */
const AlertSystem: React.FC = () => {
  // Props for the ToastContainer (excluding aria-live)
  const containerProps: ToastContainerProps = {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    newestOnTop: false,
    closeOnClick: true,
    rtl: false,
    pauseOnFocusLoss: true,
    draggable: true,
    pauseOnHover: true,
    theme: "colored",
    role: "region", // Keep role for accessibility
  };

  return (
    // Spread known props and add aria-live directly
    <ToastContainer {...containerProps} aria-live="polite" />
  );
};

export default AlertSystem; 